import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  healthDocuments,
  type HealthDocumentAnalysisData,
} from '../../services/api';
import { useToast, toastErrorMessage } from '../../context/ToastContext';
import { MobileSheet } from '../MobileSheet';
import { WORKOUT_AI_PROMPT_SHEET_Z } from '../../features/workout/workoutOverlayZIndex';
import { isRtlLocale, normalizeLocale } from '../../i18n/config';

const POLL_MS = 2000;
const MAX_POLL_ATTEMPTS = 300;

export interface HealthDocumentUploadSheetProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  testDateHint?: string;
  locale?: string;
  onSuccess?: (result: HealthDocumentAnalysisData) => void;
}

export function HealthDocumentUploadSheet({
  open,
  onClose,
  userId,
  testDateHint,
  locale,
  onSuccess,
}: HealthDocumentUploadSheetProps) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);

  const textDir = isRtlLocale(normalizeLocale(locale ?? i18n.language)) ? 'rtl' : 'ltr';

  const reset = useCallback(() => {
    setFile(null);
    setNote('');
    setUploading(false);
    setPolling(false);
    setProgressLabel('');
    setErrorText(null);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (uploading || polling) return;
    reset();
    onClose();
  }, [onClose, polling, reset, uploading]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const pollUntilDone = useCallback(async (analysisId: number): Promise<HealthDocumentAnalysisData> => {
    for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
      await new Promise(r => setTimeout(r, POLL_MS));
      const res = await healthDocuments.getStatus(analysisId);
      const data = res.data.data;
      if (!data) continue;
      if (['completed', 'partial', 'failed'].includes(data.status)) {
        return data;
      }
      setProgressLabel(t('healthDocument.state.processing'));
    }
    throw new Error(t('healthDocument.error.timeout'));
  }, [t]);

  const handleUpload = useCallback(async () => {
    if (!file) {
      const msg = t('healthDocument.error.noFile');
      setErrorText(msg);
      showToast(msg);
      return;
    }
    if (!userId || userId <= 0) {
      const msg = t('healthDocument.error.contextMissing');
      setErrorText(msg);
      showToast(msg);
      return;
    }

    setUploading(true);
    setPolling(false);
    setErrorText(null);
    setProgressLabel(t('healthDocument.state.uploading'));

    try {
      const uploadRes = await healthDocuments.analyze(file, {
        user_id: userId,
        test_date_hint: testDateHint,
        note: note.trim() || undefined,
        locale: locale ?? i18n.language?.split('-')[0] ?? 'fa',
      });
      const analysisId = uploadRes.data.data?.id;
      if (!analysisId) {
        throw new Error(t('healthDocument.error.uploadFailed'));
      }

      setUploading(false);
      setPolling(true);
      setProgressLabel(t('healthDocument.state.analyzing'));

      const result = await pollUntilDone(analysisId);

      if (result.status === 'failed') {
        const msg = result.error_message || t('healthDocument.error.analysisFailed');
        setErrorText(msg);
        showToast(msg);
        return;
      }

      const savedCount = result.saved_metrics?.length ?? 0;
      if (result.status === 'partial') {
        showToast(t('healthDocument.toast.partial', { count: savedCount }));
      } else {
        showToast(t('healthDocument.toast.saved', { count: savedCount }));
      }

      onSuccess?.(result);
      reset();
      onClose();
    } catch (err) {
      const msg = toastErrorMessage(err, t('healthDocument.error.uploadFailed'));
      setErrorText(msg);
      showToast(msg);
    } finally {
      setUploading(false);
      setPolling(false);
      setProgressLabel('');
    }
  }, [
    file,
    i18n.language,
    locale,
    note,
    onClose,
    onSuccess,
    pollUntilDone,
    reset,
    showToast,
    t,
    testDateHint,
    userId,
  ]);

  const busy = uploading || polling;

  return (
    <MobileSheet
      open={open}
      onClose={handleClose}
      title={t('healthDocument.title')}
      subtitle={t('healthDocument.subtitle')}
      zIndex={WORKOUT_AI_PROMPT_SHEET_Z}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            style={{
              padding: '10px 16px', borderRadius: 8, border: '1px solid var(--surface-sunken)',
              background: 'var(--surface-1)', cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void handleUpload()}
            disabled={busy || !file}
            style={{
              padding: '10px 18px', borderRadius: 8, border: 'none',
              background: 'var(--color-accent)', color: '#fff', fontWeight: 700,
              opacity: busy || !file ? 0.6 : 1, cursor: busy || !file ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? t('healthDocument.action.processing') : t('healthDocument.action.upload')}
          </button>
        </div>
      }
    >
      <div dir={textDir} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ fontSize: 12, color: 'var(--fg-3)', margin: 0, lineHeight: 1.5 }}>
          {t('healthDocument.hint')}
        </p>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-2)', display: 'block', marginBottom: 6 }}>
            {t('healthDocument.fields.file')}
          </label>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
            disabled={busy}
            onChange={e => setFile(e.target.files?.[0] ?? null)}
          />
          {file && (
            <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 6 }}>{file.name}</div>
          )}
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-2)', display: 'block', marginBottom: 6 }}>
            {t('healthDocument.fields.note')}
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={busy}
            rows={2}
            placeholder={t('healthDocument.fields.notePlaceholder')}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 8,
              border: '1px solid var(--surface-sunken)', fontSize: 12,
            }}
          />
        </div>

        {progressLabel && (
          <div style={{ fontSize: 12, color: 'var(--fg-2)', fontWeight: 600 }}>{progressLabel}</div>
        )}

        {errorText && (
          <div style={{
            color: '#dc2626', fontSize: 11, fontWeight: 600, padding: '8px 10px',
            background: '#fee2e2', borderRadius: 6,
          }}>
            {errorText}
          </div>
        )}
      </div>
    </MobileSheet>
  );
}
